import I18n from 'i18n-js';
import React from 'react';
import { IUserContext, withUserContext } from '@reverbdotcom/commons/src/components/user_context_provider';
import bind from '@reverbdotcom/commons/src/bind';
import { HeartFilledIcon } from '@reverbdotcom/cadence/icons/react';
import { HeartEmptyIcon } from '@reverbdotcom/cadence/icons/react';
import { RCIcon } from '@reverbdotcom/cadence/components/RCIcon/RCIcon';
import { RCToast } from '@reverbdotcom/cadence/components';
import CoreLink from '@reverbdotcom/commons/src/components/core_link';
import classNames from 'classnames';
import { Paths } from '../shared/url_helpers';
import { ToastCloseType } from '@reverbdotcom/cadence/components/types';
import { MParticleEventName, trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { resolveCurrentView, resolveFavoriteType } from '@reverbdotcom/commons/src/event_tracking/favorites';
import { withRouter, WithRouterProps } from 'react-router';

interface ExternalProps extends IUserContext {
  openModal: () => void;
  disabled: boolean;
  isFavorited: boolean;
  entityType: string;
  disabledTooltipText?: string;
  renderWithBanner: boolean;
  hasOwnToast?: boolean; // Remove when FollowButtonContainer is deprecated
  toastOpen?: boolean; // Remove when FollowButtonContainer is deprecated
  hideToast?: () => void; // Remove when FollowButtonContainer is deprecated
}

type Props = ExternalProps & WithRouterProps;

export class FavoriteButton extends React.Component<Props, null> {
  @bind
  handleClick(e) {
    e.preventDefault();

    if (this.props.disabled) {
      return;
    }

    this.props.openModal();
  }

  favoriteType() {
    return I18n.t(`discovery.favorites.favoriteButton.entityTypes.${this.props.entityType}`);
  }

  linkText() {
    if (this.props.isFavorited) {
      return I18n.t('discovery.favorites.favoriteButton.hasFavorited', { type: this.favoriteType() });
    }

    return I18n.t('discovery.favorites.favoriteButton.addFavorite', { type: this.favoriteType() });
  }

  tooltipText() {
    if (!this.props.disabled) { return null; }

    return this.props.disabledTooltipText ||
      I18n.t('discovery.favorites.favoriteButton.tooltipText');
  }

  buttonClass() {
    if (this.props.entityType === 'product') { return 'secondary'; }
    if (this.props.renderWithBanner) { return 'secondary'; }
    return 'link';
  }

  render() {
    const { disabled, isFavorited, entityType, toastOpen, hideToast } = this.props;
    const favoriteEventProperties = {
      eventName: MParticleEventName.ClickedToastMessage,
      currentView: resolveCurrentView(this.props.location),
      favoriteType: resolveFavoriteType(this.props.location),
    };

    const handleToastClose = (closeType: ToastCloseType) => {
      hideToast();
      if (closeType === 'dismiss' || closeType === 'swipe') {
        trackEvent(favoriteEventProperties);
      }
    };

    if (!entityType) { return null; }

    const classes = classNames(
      'favorite-button__button',
      {
        'favorite-button__button--link': this.buttonClass() === 'link',
        'favorite-button__button--secondary': this.buttonClass() === 'secondary',
        'favorite-button__button--favorited': this.buttonClass() !== 'link' && isFavorited,
        'favorite-button__button--disabled': disabled,
      },
    );

    return (
      <>
        <button
          className={classes}
          onClick={this.handleClick}
          type="button"
          data-disabled={disabled || null}
        >
          <RCIcon svgComponent={isFavorited ? HeartFilledIcon : HeartEmptyIcon} inline />
          {this.linkText()}
        </button>
        {
          this.props.hasOwnToast &&
          <RCToast
            isOpen={toastOpen}
            onClose={handleToastClose}
            text={I18n.t('discovery.favorites.toast.added')}
            theme="watch-add"
            link={
              <CoreLink
                to={Paths.myFavoritesSearches.expand({})}
                clickEvent={favoriteEventProperties}
              >
                {I18n.t('cadence.RCToast.linkDefault')}
              </CoreLink>
            }
          />
        }
      </>
    );
  }
}

export default withRouter(withUserContext(FavoriteButton));
